class SavedDesignsGallery extends HTMLElement {
  constructor() {
    super();

    this._onDesignsUpdated = this._onDesignsUpdated.bind(this);
    this._updateLabelHandler = this._updateLabel.bind(this);
    this.designStorage = new window.DesignStorage();
    this.parentContainer = null;
    this.titleElement = null;
    this.productId = null;
    this.cardTemplate = null;
    this.galleryContainer = null;
  }

  async connectedCallback() {
    this.productId = this.getAttribute("data-product-id");

    if (!this.productId) {
      console.warn("SavedDesignsGallery: No product ID found.");
      this._renderMessage("No product ID found.");
      return;
    }

    const parentId = this.getAttribute("data-parent");
    this.parentContainer = document.getElementById(parentId);
    this.titleElement = this.parentContainer?.querySelector(".pm__selected-title");
    this.cardTemplate = this.querySelector(".card-template");
    this.galleryContainer = this.querySelector(".pm__dyo-custom-design-gallery-container");

    await this.render();

    // Attach global listeners
    window.addEventListener("designs:updated", this._onDesignsUpdated);
    window.addEventListener("designs:deleted", this._onDesignsUpdated);
    window.addEventListener("dyo-design:selected", this._updateLabelHandler);
  }

  disconnectedCallback() {
    // Clean up all listeners to prevent memory leaks
    window.removeEventListener("designs:updated", this._onDesignsUpdated);
    window.removeEventListener("designs:deleted", this._onDesignsUpdated);
    window.removeEventListener("dyo-design:selected", this._updateLabelHandler);
  }

  _getImageUrl(design) {
    const urlHelper = new URL(design.imageUrl);
    urlHelper.searchParams.set("width", "150");
    urlHelper.searchParams.set("height", "150");
    return urlHelper.toString();
  }

  _toggleParentContainer(show) {
    if (this.parentContainer) {
      this.parentContainer.style.display = show ? "" : "none";
    }
  }

  _updateLabel(event) {
    if (!this.titleElement) return;

    const { config } = event.detail?.summary || {};
    if (!config) return;

    const designName = config
      .map((c) => c.materialType)
      .filter(Boolean)
      .join(" + ") || "";
      
    this.titleElement.textContent = designName;
  }

  async _onDesignsUpdated() {
    await this.render();
  }

  async render() {
    let designs;

    try {
      designs = await this.designStorage.get(this.productId);
    } catch (e) {
      console.error("Failed to load designs from IndexedDB:", e);
      this._toggleParentContainer(false);
      return;
    }

    if (!designs || designs.length === 0) {
      this._toggleParentContainer(false);
      return;
    }

    this._renderGallery(designs);
    this._toggleParentContainer(true);
  }

  _renderGallery(designs) {
    if (!this.galleryContainer) {
      console.warn("SavedDesignsGallery: Gallery container not found.");
      return;
    }

    const renderedItems = designs.map((design) => this._renderDesignItem(design));

    if (renderedItems.length === 0) {
      console.warn("SavedDesignsGallery: No designs to render.");
      return;
    }

    this.galleryContainer.innerHTML = renderedItems.join("");
  }

  _renderDesignItem(design) {
    if (!this.cardTemplate) {
      console.warn("SavedDesignsGallery: Card template not found.");
      return "";
    }

    const cardTemplateContent = this.cardTemplate.content;
    const cardElement = cardTemplateContent
      .querySelector("custom-design-card")
      .cloneNode(true);

    cardElement.setAttribute("data-design-id", design.id);
    cardElement.setAttribute("data-product-id", design.productId);
    cardElement.setAttribute("data-summary", JSON.stringify(design));

    // This is read by _handleCardClick
    const productUrl = design.config?.find((item) => item.productUrl)?.productUrl || '';
    const variantId = design.config?.find((item) => item.combinationId)?.combinationId || '';

    if (productUrl && variantId) {
      cardElement.setAttribute("data-href", `${productUrl}?variant=${variantId}`);
    }

    const imgElement = cardElement.querySelector(".pm__big-selector-img");
    if (imgElement && design.imageUrl) {
      imgElement.src = this._getImageUrl(design);
    }

    return cardElement.outerHTML;
  }
}

if (!customElements.get("saved-designs-gallery")) {
  customElements.define("saved-designs-gallery", SavedDesignsGallery);
}